// Cookie / tracking consent for NORYQ.
//
// Rules this file enforces:
//   1. Nothing non-essential (analytics, ads) loads before the visitor opts in.
//   2. The choice is stored with a version + timestamp (proof of consent).
//   3. Withdrawing consent is as easy as giving it (see ConsentBanner).
//
// Bump CONSENT_VERSION whenever you add a new tracker or change what a category
// covers. Everyone will then be asked again.

import { useEffect, useState } from "react";

export const CONSENT_VERSION = 1;
const STORAGE_KEY = "noryq-consent";
const CHANGE_EVENT = "noryq:consent-changed";
const OPEN_EVENT = "noryq:open-consent";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "G-RTSZJ7689K";

// ── Read / write ────────────────────────────────────────────────────────────

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null; // re-ask
    return {
      version: parsed.version,
      analytics: !!parsed.analytics,
      advertising: !!parsed.advertising,
      decidedAt: parsed.decidedAt,
    };
  } catch {
    return null;
  }
}

export function saveConsent({ analytics, advertising }) {
  const previous = getConsent();
  const consent = {
    version: CONSENT_VERSION,
    analytics: !!analytics,
    advertising: !!advertising,
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Storage blocked: the choice still applies for this page view.
  }
  applyConsent(consent);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: consent }));

  // Third-party ad scripts that already ran can't be "un-run". If advertising
  // consent is being withdrawn, reload so they are really gone.
  if (previous?.advertising && !consent.advertising) {
    setTimeout(() => window.location.reload(), 200);
  }
  return consent;
}

// ── Open the preferences panel from anywhere ────────────────────────────────

export function openConsentSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onOpenConsentSettings(handler) {
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}

// ── React hook ──────────────────────────────────────────────────────────────

export function useConsent() {
  const [consent, setConsent] = useState(getConsent);
  useEffect(() => {
    const handler = () => setConsent(getConsent());
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, []);
  return consent;
}

// ── Google Analytics (loaded only after consent) ────────────────────────────

let gaLoaded = false;

function loadGoogleAnalytics() {
  if (!GA_ID) return;
  window[`ga-disable-${GA_ID}`] = false;
  if (gaLoaded) return;
  gaLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", GA_ID, {
    // Keep it lean: no ad personalisation, no cross-site Google signals.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

function expireCookie(name) {
  const past = "Thu, 01 Jan 1970 00:00:00 GMT";
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [host, `.${host}`];
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join(".")}`);
  domains.forEach((d) => {
    document.cookie = `${name}=; expires=${past}; path=/; domain=${d}`;
  });
  document.cookie = `${name}=; expires=${past}; path=/`;
}

function disableGoogleAnalytics() {
  if (GA_ID) window[`ga-disable-${GA_ID}`] = true;
  document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter((n) => n === "_ga" || n.startsWith("_ga_") || n === "_gid")
    .forEach(expireCookie);
}

// Called on every app start and every time the visitor changes their choice.
export function applyConsent(consent = getConsent()) {
  if (consent?.analytics) {
    loadGoogleAnalytics();
  } else {
    disableGoogleAnalytics();
  }
  // Advertising: ad components read useConsent() and render nothing
  // unless consent.advertising is true, so there is nothing to load here.
}
